import { flatten, isEmpty} from 'lodash'
import {
  BANNER_IMAGE_MODULE, DEFAULT_TIMEZONE, EmailAttributes, LEFT_SQUARE,
  RasaEmailFooterImages, RIGHT_SQUARE, SQUARE_IMAGE_MODULE
} from './constants'
import { TemplateModule } from './types/email_layout'

declare const RASA_IMAGE_API_ENDPOINT: string

const BLACK = 'black'
const WHITE = 'white'
const WHITE_COLORS = [
  WHITE,
  'ffffff',
]
const DARK_COLORS = [
  BLACK,
  '000000',
]
const ELASTIC_SEARCH_SUPPORTED_TIMEZONES = [
  "Africa/Accra", "Africa/Cairo", "Africa/Casablanca", "Africa/Douala", "Africa/Johannesburg",
  "Africa/Lagos", "Africa/Nairobi", "Africa/Porto-Novo", "Africa/Tunis", "America/Anchorage",
  "America/Argentina/Buenos_Aires", "America/Argentina/Cordoba", "America/Aruba", "America/Belem",
  "America/Bogota", "America/Boise", "America/Caracas", "America/Chicago", "America/Costa_Rica",
  "America/Cuiaba", "America/Denver", "America/Detroit", "America/Edmonton", "America/Fortaleza",
  "America/Halifax", "America/Indiana/Indianapolis", "America/Indiana/Knox", "America/Kentucky/Louisville",
  "America/Lima", "America/Los_Angeles", "America/Martinique", "America/Mexico_City", "America/Montreal",
  "America/New_York", "America/Panama", "America/Phoenix", "America/Port-au-Prince", "America/Port_of_Spain",
  "America/Puerto_Rico", "America/Regina", "America/Santiago", "America/Sao_Paulo", "America/Tijuana",
  "America/Toronto", "America/Vancouver", "America/Winnipeg", "Asia/Bangkok", "Asia/Beirut", "Asia/Calcutta",
  "Asia/Chongqing", "Asia/Damascus", "Asia/Dubai", "Asia/Hong_Kong", "Asia/Jakarta", "Asia/Jerusalem",
  "Asia/Kathmandu", "Asia/Kolkata", "Asia/Krasnoyarsk", "Asia/Kuala_Lumpur", "Asia/Kuwait", "Asia/Manila",
  "Asia/Muscat", "Asia/Qatar", "Asia/Riyadh", "Asia/Saigon", "Asia/Seoul", "Asia/Shanghai", "Asia/Singapore",
  "Asia/Taipei", "Asia/Tokyo", "Asia/Ulaanbaatar", "Asia/Vientiane", "Atlantic/Canary", "Australia/ACT",
  "Australia/Adelaide", "Australia/Brisbane", "Australia/Darwin", "Australia/Hobart", "Australia/Melbourne",
  "Australia/NSW", "Australia/Perth", "Australia/Queensland", "Australia/Sydney", "Australia/Victoria",
  "Australia/West", "Brazil/East", "Canada/Atlantic", "Canada/Central", "Canada/Eastern", "Canada/Mountain",
  "Canada/Pacific", "Chile/EasterIsland", "Europe/Amsterdam", "Europe/Athens", "Europe/Belfast", "Europe/Belgrade",
  "Europe/Berlin", "Europe/Brussels", "Europe/Bucharest", "Europe/Budapest", "Europe/Copenhagen", "Europe/Dublin",
  "Europe/Guernsey", "Europe/Helsinki", "Europe/Istanbul", "Europe/Kiev", "Europe/Lisbon", "Europe/Ljubljana",
  "Europe/London", "Europe/Luxembourg", "Europe/Madrid", "Europe/Malta", "Europe/Moscow", "Europe/Oslo",
  "Europe/Paris", "Europe/Prague", "Europe/Riga", "Europe/Rome", "Europe/Skopje", "Europe/Sofia", "Europe/Stockholm",
  "Europe/Tallinn", "Europe/Tirane", "Europe/Vienna", "Europe/Warsaw", "Europe/Zagreb", "Europe/Zurich",
  "Asia/Calcutta", "Pacific/Auckland", "Pacific/Easter", "Pacific/Honolulu", "UTC", "Universal"
];

const ES_TO_RASA_TIMEZONE_MAPPING = {
  "CET": "Europe/Berlin", 
  "GMT": "Etc/Greenwich", 
  "EE": "Europe/Tallinn", 
  "EST": "America/New_York", 
  "EST5EDT": "America/New_York", 
  "Egypt": "Africa/Cairo", 
  "Etc/GMT": "Etc/Greenwich", 
  "Etc/GMT+0": "Etc/Greenwich", 
  "Etc/GMT+1": "Etc/GMT+1", 
  "Etc/GMT+2": "Etc/GMT+2", 
  "Etc/GMT+3": "Etc/GMT+3", 
  "Etc/GMT-2": "Etc/GMT-2", 
  "Etc/GMT-5": "Etc/GMT-5", 
  "Greenwich": "Etc/Greenwich",
  "Hongkong": "Asia/Hong_Kong",
  "Israel": "Asia/Jerusalem",
  "Japan": "Asia/Tokyo",
  "MST": "America/Denver",
  "NZ": "Pacific/Auckland",
  "PST8PDT": "America/Los_Angeles",
  "Turkey": "Europe/Istanbul",
  "UCT": "UTC"
};

export const convertHexToRGB = (hex: string): number[] => {
  const aRgbHex = hex.slice(1).match(/.{1,2}/g)
  const aRgb = [
      parseInt(aRgbHex[0], 16),
      parseInt(aRgbHex[1], 16),
      parseInt(aRgbHex[2], 16),
  ]
  return aRgb
}

export const deltaE = (rgbA, rgbB) => {
  const labA = rgb2lab(rgbA)
  const labB = rgb2lab(rgbB)
  const deltaL = labA[0] - labB[0]
  const deltaA = labA[1] - labB[1]
  const deltaB = labA[2] - labB[2]
  const c1 = Math.sqrt(labA[1] * labA[1] + labA[2] * labA[2])
  const c2 = Math.sqrt(labB[1] * labB[1] + labB[2] * labB[2])
  const deltaC = c1 - c2
  let deltaH = deltaA * deltaA + deltaB * deltaB - deltaC * deltaC
  deltaH = deltaH < 0 ? 0 : Math.sqrt(deltaH)
  const sc = 1.0 + 0.045 * c1
  const sh = 1.0 + 0.015 * c1
  const deltaLKlsl = deltaL / (1.0)
  const deltaCkcsc = deltaC / (sc)
  const deltaHkhsh = deltaH / (sh)
  const i = deltaLKlsl * deltaLKlsl + deltaCkcsc * deltaCkcsc + deltaHkhsh * deltaHkhsh
  return i < 0 ? 0 : Math.sqrt(i)
}

export const rgb2lab = (rgb) => {
  let r = rgb[0] / 255
  let g = rgb[1] / 255
  let b = rgb[2] / 255
  let x = 0
  let y = 0
  let z = 0

  r = (r > 0.04045) ? Math.pow((r + 0.055) / 1.055, 2.4) : r / 12.92
  g = (g > 0.04045) ? Math.pow((g + 0.055) / 1.055, 2.4) : g / 12.92
  b = (b > 0.04045) ? Math.pow((b + 0.055) / 1.055, 2.4) : b / 12.92
  x = (r * 0.4124 + g * 0.3576 + b * 0.1805) / 0.95047
  y = (r * 0.2126 + g * 0.7152 + b * 0.0722) / 1.00000
  z = (r * 0.0193 + g * 0.1192 + b * 0.9505) / 1.08883
  x = (x > 0.008856) ? Math.pow(x, 1 / 3) : (7.787 * x) + 16 / 116
  y = (y > 0.008856) ? Math.pow(y, 1 / 3) : (7.787 * y) + 16 / 116
  z = (z > 0.008856) ? Math.pow(z, 1 / 3) : (7.787 * z) + 16 / 116
  return [(116 * y) - 16, 500 * (x - y), 200 * (y - z)]
}

export const areCloseColors = (color1: string, color2: string): boolean => {
  if (color1 && color2) {
    const deltaEValue = deltaE(convertHexToRGB(color1), convertHexToRGB(color2))
    return deltaEValue < 35
  } else {
    return false
  }

}

const isLightFooter = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.primaryColor)
  return color && (WHITE_COLORS.includes(color.toLowerCase().slice(1)) || areCloseColors(color , '#ffffff'))
}
const isLightFooterFont = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  return color && WHITE_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#ffffff'))
}

const isDarkFooter = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.primaryColor)
  return color && DARK_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#000000'))
}
const isDarkFooterFont = (props: any): boolean => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  return color && DARK_COLORS.includes(color.toLowerCase().slice(1) || areCloseColors(color , '#000000'))
}

const footerFontColor = (props: any): string => {
  const color = getEmailAttribute(props, EmailAttributes.footerFontColor)
  if ( isEmpty(color) ) {
    return isLightFooter(props) ? BLACK : WHITE
  } else {
    return (isDarkFooter(props) && isDarkFooterFont(props)) ? WHITE
      : (isLightFooter(props) && isLightFooterFont(props)) ? BLACK
      : areCloseColors(color, getEmailAttribute(props, EmailAttributes.primaryColor)) ?
        (isLightFooter(props) ? BLACK : WHITE)
        : color
  }
}

export const getEmailAttribute = (props: any, key: EmailAttributes) => props[key]

export const footerStyles = (props: any) => ({
  backgroundColor: getEmailAttribute(props, EmailAttributes.primaryColor),
  color: footerFontColor(props),
  fontFamily: getEmailAttribute(props, EmailAttributes.newsletterFontFamily),
})

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const footerLogo = (props: any): RasaEmailFooterImages => {
  return RasaEmailFooterImages.large
}

export interface SocialMediaIconOptions {
  requiresBlack?: boolean,
}
export const socialMediaIcon = (name: string, props: any, options: SocialMediaIconOptions = {}): string => {
  const color = options.requiresBlack || isLightFooter(props.session) ? 'black' : 'white'
  return `${RASA_IMAGE_API_ENDPOINT}/hosted-images-newsbrief/path/social-media/${name}_64_${color}_square.png`
}

export enum CapTextStrategy {
  // Important that default also be 0 - so "not specified" is same as "0" as "falsey" values
  // And these should be strings - since that's what the underlying properties are
  SENTENCE_CAP = '0',
  EXACT_CAP = '1',
  WORD_CAP = '2',
  NO_CAP = '3',
}
interface CappedText {
  isCapped: boolean,
  text: string,
}
export const capText = (text: string, maxLength: number, capStrategy: CapTextStrategy): CappedText => {
  const trimmedText: string = text ? text.trim() : ''
  switch (capStrategy) {
    case CapTextStrategy.EXACT_CAP:
      return {
        isCapped: trimmedText.length > maxLength,
        text: trimmedText.slice(0, maxLength),
      }
    case CapTextStrategy.WORD_CAP:
    case CapTextStrategy.SENTENCE_CAP:
      // Word and Sentence are implemented identically on the client right now
      if (trimmedText.length > maxLength) {
        const cappedText: string = trimmedText.slice(0, maxLength)
        const lastSpace: number = cappedText.lastIndexOf(' ')
        if (lastSpace > 0) {
          return {
            isCapped: true,
            text: trimmedText.slice(0, lastSpace).trim(),
          }
        } else {
          // no space - I guess return the trimmed text
          return {
            isCapped: true,
            text: trimmedText.slice(0, maxLength),
          }
        }
      }
    case CapTextStrategy.NO_CAP:
    default:
      return {
        isCapped: false,
        text,
      }
  }
}

interface ContentTypeIdObj {
  contentType: string
  id: string
}

export const parseTypeId = (typeId: string): ContentTypeIdObj => {
  return {
    contentType: typeId.split('-')[0],
    id: typeId.split('-')[1],
  }
}

export const combineTypeId = (contentType: string, id: number): string => {
  return `${contentType}-${id}`
}

export const needsImageUpload = (imageUrl: string): boolean => {
    if (imageUrl.includes('rasa-hosted-images') || imageUrl.includes('rasa-self-service-logos')) {
      return false
    } else {
      return true
    }
}

export const getImageDisplayName = (
  moduleType: string, sequence: number, location: string = null): string => {

  if (moduleType === BANNER_IMAGE_MODULE) {
    return getBannerImageName(sequence)
  } else if (moduleType === SQUARE_IMAGE_MODULE) {
    return getSquareImageName(sequence, location)
  } else {
    return null
  }
}

export const getBannerImageName = (sequence: number): string => {
  return `Banner ${sequence}`
}

export const getSquareImageIndex = (sequence, location: string): number => {
  // 1 -> 1,2
  // 2 -> 3,4
  // 3 -> 5,6
  if (location === LEFT_SQUARE) {
    return (sequence * 2) - 1
  } else if (location === RIGHT_SQUARE) {
    return sequence * 2
  } else {
    return 0
  }
}

export const getSquareImageName = (sequence: number, location: string): string => {
  return `Square ${getSquareImageIndex(sequence, location)}`
}

export const getSponsoredName = (sequence: number): string => {
  return `Sponsored ${sequence}`
}

export const getStaticImage = (
  staticImages: any,
  moduleType: string,
  sequence: number,
  location: string = null,
): any => {
  if (staticImages) {
    if (moduleType === BANNER_IMAGE_MODULE) {
      return getBannerImage(staticImages, sequence)
    } else if (moduleType === SQUARE_IMAGE_MODULE) {
      return getSquareStaticImage(staticImages, sequence, location)
    } else {
      return {}
    }
  } else {
    return {}
  }
}

export const getBannerImage = (allStaticImages: any[], sequence: number) => {
  const bannerImageName = getBannerImageName(sequence)
  return findStaticImage(allStaticImages, bannerImageName)
}

export const getSquareStaticImage = (allStaticImages: any[], sequence: number, location: string) => {
  const squareImageName = getSquareImageName(sequence, location)
  return findStaticImage(allStaticImages, squareImageName)
}

const findStaticImage = (allStaticImages: any[], typeName: string) => {
  if (Array.isArray(allStaticImages)) {
    return allStaticImages.find((x) => x.name === typeName) || {}
  } else {
    return Object.keys(allStaticImages)
                 .map((key) => allStaticImages[key])
                 .find((x: any) => x.name === typeName) || {}
  }

}

export const isCustomTemplate = (emailTemplate: string): boolean => {
  emailTemplate = emailTemplate || ''
  if (emailTemplate.indexOf('custom:') > -1) {
    return true
  }
  return false
}

export interface LocationOption {
  key: string,
  value: string,
}

// We may call an sp to get this value but, this is static data so keeping as constant for now.
export const getAllImageLocationsOptions = (): Promise<LocationOption[]> => {
  // limit to Banner = 7
  // limit Square = 14 i.e ( 7 * 2)
  const BANNER_IMAGE_COUNT = 7
  const SQUARE_IMAGE_COUNT = 7
  const bannerImageLocationOptions = [...Array(BANNER_IMAGE_COUNT).keys()].map((i) =>
    ([{key: getBannerImageName(i + 1), value: getBannerImageName(i + 1)}]))
  const squareImageLocationOptions = [...Array(SQUARE_IMAGE_COUNT).keys()].map((i) => {
    const leftImageName = getSquareImageName(i + 1, LEFT_SQUARE)
    const rightImageName = getSquareImageName(i + 1, RIGHT_SQUARE)
    return [
      {
        key: leftImageName,
        value: leftImageName,
      },
      {
        key: rightImageName,
        value: rightImageName,
      },
    ]
  })
  return Promise.resolve(flatten(bannerImageLocationOptions.concat(squareImageLocationOptions)))
}

export const getBannerImageLocationOptions = (): Promise<LocationOption[]> => {
  // limit to Banner = 7
  const BANNER_IMAGE_COUNT = 7
  const bannerImageLocationOptions = [...Array(BANNER_IMAGE_COUNT).keys()].map((i) =>
    ([{key: getBannerImageName(i + 1), value: getBannerImageName(i + 1)}]))
  return Promise.resolve(flatten(bannerImageLocationOptions))
}

export const getSquareImageLocationsOptions = (): Promise<LocationOption[]> => {
  // limit Square = 14 i.e ( 7 * 2)
  const SQUARE_IMAGE_COUNT = 7
  const squareImageLocationOptions = [...Array(SQUARE_IMAGE_COUNT).keys()].map((i) => {
    const leftImageName = getSquareImageName(i + 1, LEFT_SQUARE)
    const rightImageName = getSquareImageName(i + 1, RIGHT_SQUARE)
    return [
      {
        key: leftImageName,
        value: leftImageName,
      },
      {
        key: rightImageName,
        value: rightImageName,
      },
    ]
  })
  return Promise.resolve(flatten(squareImageLocationOptions))
}

export const getAllTextLocationsOptions = (): Promise<LocationOption[]> => {
  // limit to 7 Text
  const TEXT_COUNT = 7
  return Promise.resolve([...Array(TEXT_COUNT).keys()].map((i) => ({key: getTextName(i + 1), value: `Text${i + 1}`})))
}

export const getImageLocationOptions = (emailLayout: any): Promise<LocationOption[]> => {
  // Wrap in a promise if we ever go to db to get all locations
  const templateModules: TemplateModule[] = emailLayout.template_modules
  if (templateModules && !isCustomTemplate(emailLayout.email_template)) {
    const imageModules = templateModules.filter((tm) => tm.type.toLocaleLowerCase().includes('image'))
    const imageLocationOptions = imageModules.map((im) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
      if (im.type === 'image') {
        const bannerName = getBannerImageName(im.sequence)
        return {
          key: bannerName,
          value: bannerName,
        }
      } else {
        const leftImageName = getSquareImageName(im.sequence, LEFT_SQUARE)
        const rightImageName = getSquareImageName(im.sequence, RIGHT_SQUARE)
        return [
          {
            key: leftImageName,
            value: leftImageName,
          },
          {
            key: rightImageName,
            value: rightImageName,
          },
        ]
      }
    })
    const sortedImageLocationOptions = flatten(imageLocationOptions).sort((a, b) => {
      if (a.value < b.value) {
        return -1
      }
      if (a.value > b.value) {
        return 1
      }
      return 0
    })
    return Promise.resolve(sortedImageLocationOptions)
  } else {
    return getAllImageLocationsOptions()
  }
}

export const getTextName = (sequence: number): string => `Text ${sequence}`

export const getTextLocationOptions = (emailLayout: any): Promise<LocationOption[]> => {
  const templateModules: TemplateModule[] = emailLayout.template_modules
  if (templateModules && !isCustomTemplate(emailLayout.email_template)) {
    const textModules = templateModules.filter((tm) => tm.type.toLocaleLowerCase().includes('text'))
    const textLocationOptions = textModules.map((im) => ({
      key: getTextName(im.sequence),
      value: `Text${im.sequence}`,
    }))
    const sortedTextLocationOptions = textLocationOptions.sort((a, b) => {
      if (a.value < b.value) {
        return -1
      }
      if (a.value > b.value) {
        return 1
      }
      return 0
    })
    return Promise.resolve(sortedTextLocationOptions)
  } else {
    return getAllTextLocationsOptions()
  }

}
export const loadTimeZones = (): Promise<any> => {
  const timeZoneList = [{"timezone":"Africa/Accra"},{"timezone":"Africa/Cairo"},{"timezone":"Africa/Casablanca"},{"timezone":"Africa/Douala"},{"timezone":"Africa/Johannesburg"},{"timezone":"Africa/Lagos"},{"timezone":"Africa/Nairobi"},{"timezone":"Africa/Porto-Novo"},{"timezone":"Africa/Tunis"},{"timezone":"America/Anchorage"},{"timezone":"America/Argentina/Buenos_Aires"},{"timezone":"America/Argentina/Cordoba"},{"timezone":"America/Aruba"},{"timezone":"America/Belem"},{"timezone":"America/Bogota"},{"timezone":"America/Boise"},{"timezone":"America/Caracas"},{"timezone":"America/Chicago"},{"timezone":"America/Costa_Rica"},{"timezone":"America/Cuiaba"},{"timezone":"America/Denver"},{"timezone":"America/Detroit"},{"timezone":"America/Edmonton"},{"timezone":"America/Fortaleza"},{"timezone":"America/Halifax"},{"timezone":"America/Indiana/Indianapolis"},{"timezone":"America/Indiana/Knox"},{"timezone":"America/Kentucky/Louisville"},{"timezone":"America/Lima"},{"timezone":"America/Los_Angeles"},{"timezone":"America/Martinique"},{"timezone":"America/Mexico_City"},{"timezone":"America/Montreal"},{"timezone":"America/New_York"},{"timezone":"America/Panama"},{"timezone":"America/Phoenix"},{"timezone":"America/Port-au-Prince"},{"timezone":"America/Port_of_Spain"},{"timezone":"America/Puerto_Rico"},{"timezone":"America/Regina"},{"timezone":"America/Santiago"},{"timezone":"America/Sao_Paulo"},{"timezone":"America/Tijuana"},{"timezone":"America/Toronto"},{"timezone":"America/Vancouver"},{"timezone":"America/Winnipeg"},{"timezone":"Asia/Bangkok"},{"timezone":"Asia/Beirut"},{"timezone":"Asia/Calcutta"},{"timezone":"Asia/Chongqing"},{"timezone":"Asia/Damascus"},{"timezone":"Asia/Dubai"},{"timezone":"Asia/Hong_Kong"},{"timezone":"Asia/Jakarta"},{"timezone":"Asia/Jerusalem"},{"timezone":"Asia/Kathmandu"},{"timezone":"Asia/Kolkata"},{"timezone":"Asia/Krasnoyarsk"},{"timezone":"Asia/Kuala_Lumpur"},{"timezone":"Asia/Kuwait"},{"timezone":"Asia/Manila"},{"timezone":"Asia/Muscat"},{"timezone":"Asia/Qatar"},{"timezone":"Asia/Riyadh"},{"timezone":"Asia/Saigon"},{"timezone":"Asia/Seoul"},{"timezone":"Asia/Shanghai"},{"timezone":"Asia/Singapore"},{"timezone":"Asia/Taipei"},{"timezone":"Asia/Tokyo"},{"timezone":"Asia/Ulaanbaatar"},{"timezone":"Asia/Vientiane"},{"timezone":"Atlantic/Canary"},{"timezone":"Australia/ACT"},{"timezone":"Australia/Adelaide"},{"timezone":"Australia/Brisbane"},{"timezone":"Australia/Darwin"},{"timezone":"Australia/Hobart"},{"timezone":"Australia/Melbourne"},{"timezone":"Australia/NSW"},{"timezone":"Australia/Perth"},{"timezone":"Australia/Queensland"},{"timezone":"Australia/Sydney"},{"timezone":"Australia/Victoria"},{"timezone":"Australia/West"},{"timezone":"Brazil/East"},{"timezone":"CET"},{"timezone":"Canada/Atlantic"},{"timezone":"Canada/Central"},{"timezone":"Canada/Eastern"},{"timezone":"Canada/Mountain"},{"timezone":"Canada/Pacific"},{"timezone":"Chile/EasterIsland"},{"timezone":"EE"},{"timezone":"EST"},{"timezone":"EST5EDT"},{"timezone":"Egypt"},{"timezone":"Etc/GMT"},{"timezone":"Etc/GMT+0"},{"timezone":"Etc/GMT+1"},{"timezone":"Etc/GMT+2"},{"timezone":"Etc/GMT+3"},{"timezone":"Etc/GMT+5"},{"timezone":"Etc/GMT-2"},{"timezone":"Etc/GMT-5"},{"timezone":"Etc/Greenwich"},{"timezone":"Europe/Amsterdam"},{"timezone":"Europe/Athens"},{"timezone":"Europe/Belfast"},{"timezone":"Europe/Belgrade"},{"timezone":"Europe/Berlin"},{"timezone":"Europe/Brussels"},{"timezone":"Europe/Bucharest"},{"timezone":"Europe/Budapest"},{"timezone":"Europe/Copenhagen"},{"timezone":"Europe/Dublin"},{"timezone":"Europe/Guernsey"},{"timezone":"Europe/Helsinki"},{"timezone":"Europe/Istanbul"},{"timezone":"Europe/Kiev"},{"timezone":"Europe/Lisbon"},{"timezone":"Europe/Ljubljana"},{"timezone":"Europe/London"},{"timezone":"Europe/Luxembourg"},{"timezone":"Europe/Madrid"},{"timezone":"Europe/Malta"},{"timezone":"Europe/Moscow"},{"timezone":"Europe/Oslo"},{"timezone":"Europe/Paris"},{"timezone":"Europe/Prague"},{"timezone":"Europe/Riga"},{"timezone":"Europe/Rome"},{"timezone":"Europe/Skopje"},{"timezone":"Europe/Sofia"},{"timezone":"Europe/Stockholm"},{"timezone":"Europe/Tallinn"},{"timezone":"Europe/Tirane"},{"timezone":"Europe/Vienna"},{"timezone":"Europe/Warsaw"},{"timezone":"Europe/Zagreb"},{"timezone":"Europe/Zurich"},{"timezone":"GB"},{"timezone":"GB-Eire"},{"timezone":"GMT"},{"timezone":"GMT+0"},{"timezone":"Greenwich"},{"timezone":"Hongkong"},{"timezone":"Indian/Cocos"},{"timezone":"Indian/Mahe"},{"timezone":"Indian/Maldives"},{"timezone":"Indian/Reunion"},{"timezone":"Israel"},{"timezone":"Japan"},{"timezone":"MET"},{"timezone":"MST"},{"timezone":"NZ"},{"timezone":"PST8PDT"},{"timezone":"Pacific/Auckland"},{"timezone":"Pacific/Easter"},{"timezone":"Pacific/Honolulu"},{"timezone":"Poland"},{"timezone":"Singapore"},{"timezone":"Turkey"},{"timezone":"UCT"},{"timezone":"US/Aleutian"},{"timezone":"US/Arizona"},{"timezone":"US/Central"},{"timezone":"US/East-Indiana"},{"timezone":"US/Eastern"},{"timezone":"US/Hawaii"},{"timezone":"US/Mountain"},{"timezone":"US/Pacific"},{"timezone":"UTC"},{"timezone":"Universal"}]
  return Promise.resolve(timeZoneList)

}

export const getTimeZoneForES = (timezone: string): string =>  {
  if (ELASTIC_SEARCH_SUPPORTED_TIMEZONES.includes(timezone)) {
    return timezone;
  }
  const mappedTimeZone = ES_TO_RASA_TIMEZONE_MAPPING[timezone];
  if (mappedTimeZone) {
    return mappedTimeZone;
  }
  return DEFAULT_TIMEZONE;
}